import { useEffect, useState } from 'react';
import {
  HomePageStarredEntities,
  HomePageRecentlyVisited,
  HomePageTopVisited,
  HomePageToolkit,
  WelcomeTitle,
} from '@backstage/plugin-home';
import {
  makeStyles,
  Grid,
  Card,
  CardContent,
  Typography,
} from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef, EntityRefLinks } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import CategoryIcon from '@material-ui/icons/Category';
import ExtensionIcon from '@material-ui/icons/Extension';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import LibraryAddIcon from '@material-ui/icons/LibraryAdd';
import NotificationsIcon from '@material-ui/icons/Notifications';
import SearchIcon from '@material-ui/icons/Search';
import PeopleIcon from '@material-ui/icons/People';
import CodeIcon from '@material-ui/icons/Code';
import BugReportIcon from '@material-ui/icons/BugReport';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import BusinessIcon from '@material-ui/icons/Business';

const useStyles = makeStyles(theme => ({
  header: {
    background: `linear-gradient(135deg, ${
      theme.palette.type === 'dark' ? '#1a1a2e' : '#5e35b1'
    } 0%, ${theme.palette.type === 'dark' ? '#16213e' : '#1e88e5'} 100%)`,
    padding: theme.spacing(4, 0),
    marginBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  greeting: {
    color: '#fff',
    marginBottom: theme.spacing(2),
    fontWeight: 300,
    fontSize: '1.5rem',
    textAlign: 'center',
    '& span': {
      color: '#fff',
    },
  },
  searchContainer: {
    width: '100%',
    maxWidth: 600,
    position: 'relative',
  },
  searchInput: {
    width: '100%',
    padding: theme.spacing(1.5, 2, 1.5, 5),
    borderRadius: 50,
    border: 'none',
    fontSize: '1rem',
    outline: 'none',
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: '#fff',
    '&::placeholder': {
      color: 'rgba(255,255,255,0.7)',
    },
    '&:focus': {
      backgroundColor: 'rgba(255,255,255,0.25)',
    },
  },
  searchIconWrapper: {
    position: 'absolute',
    left: theme.spacing(2),
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'rgba(255,255,255,0.7)',
    display: 'flex',
  },
  content: {
    padding: theme.spacing(0, 3),
  },
  statsCard: {
    height: '100%',
  },
  statsGrid: {
    textAlign: 'center',
    padding: theme.spacing(1),
  },
  statsNumber: {
    fontSize: '2rem',
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  statsLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
}));

type CatalogStats = {
  components: number;
  apis: number;
  groups: number;
  users: number;
  systems: number;
  resources: number;
};

const useCatalogStats = () => {
  const catalogApi = useApi(catalogApiRef);
  const [stats, setStats] = useState<CatalogStats | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      const kinds = [
        'component',
        'api',
        'group',
        'user',
        'system',
        'resource',
      ] as const;
      // totalItems es el total real; el endpoint /entities no devuelve x-total-count y contaba solo la página.
      const counts = await Promise.all(
        kinds.map(async kind => {
          const res = await catalogApi.queryEntities({ filter: { kind }, limit: 1 });
          return res.totalItems;
        }),
      );
      setStats({
        components: counts[0],
        apis: counts[1],
        groups: counts[2],
        users: counts[3],
        systems: counts[4],
        resources: counts[5],
      });
    };
    fetchStats();
  }, [catalogApi]);

  return stats;
};

const CatalogStatsCard = () => {
  const classes = useStyles();
  const stats = useCatalogStats();

  const items = stats
    ? [
        { label: 'Components', count: stats.components },
        { label: 'APIs', count: stats.apis },
        { label: 'Groups', count: stats.groups },
        { label: 'Users', count: stats.users },
        { label: 'Systems', count: stats.systems },
        { label: 'Resources', count: stats.resources },
      ]
    : [];

  return (
    <Card className={classes.statsCard}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Catalog Stats
        </Typography>
        {stats ? (
          <Grid container spacing={1}>
            {items.map(item => (
              <Grid item xs={4} key={item.label} className={classes.statsGrid}>
                <Typography className={classes.statsNumber}>
                  {item.count}
                </Typography>
                <Typography className={classes.statsLabel}>
                  {item.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography color="textSecondary">Loading...</Typography>
        )}
      </CardContent>
    </Card>
  );
};

/** Servicios de los equipos del usuario: el punto de entrada a su vista integral. */
const MyServicesCard = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const [services, setServices] = useState<Entity[] | null>(null);

  useEffect(() => {
    const load = async () => {
      const { ownershipEntityRefs } = await identityApi.getBackstageIdentity();
      const groups = ownershipEntityRefs.filter(ref => ref.startsWith('group:'));
      if (!groups.length) return setServices([]);
      const { items } = await catalogApi.getEntities({
        filter: { kind: 'component', 'relations.ownedBy': groups },
        fields: ['kind', 'metadata.name', 'metadata.namespace', 'spec.type'],
        order: { field: 'metadata.name', order: 'asc' },
      });
      return setServices(items);
    };
    load().catch(() => setServices([]));
  }, [catalogApi, identityApi]);

  return (
    <Card className={classes.statsCard}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Mis servicios {services ? `(${services.length})` : ''}
        </Typography>
        {!services && <Typography color="textSecondary">Cargando...</Typography>}
        {services?.length === 0 && (
          <Typography color="textSecondary">Tus equipos aún no tienen servicios asignados.</Typography>
        )}
        {!!services?.length && <EntityRefLinks entityRefs={services} defaultKind="component" />}
      </CardContent>
    </Card>
  );
};

export const HomePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const quickLinks = [
    { label: 'Catalog', url: '/catalog', icon: <CategoryIcon /> },
    { label: 'APIs', url: '/api-docs', icon: <ExtensionIcon /> },
    {
      label: 'My Groups',
      url: '/catalog?filters[kind]=group&filters[user]=owned',
      icon: <PeopleIcon />,
    },
    { label: 'Android', url: '/android-releases', icon: <PhoneAndroidIcon /> },
    { label: 'Tech Radar', url: '/tech-radar', icon: <TrackChangesIcon /> },
    { label: 'Autoservicio', url: '/create', icon: <LibraryAddIcon /> },
    {
      label: 'Notifications',
      url: '/notifications',
      icon: <NotificationsIcon />,
    },
  ];

  const externalLinks = [
    {
      label: 'GitHub Deuna Platform',
      url: 'https://github.com/deuna-platform',
      icon: <CodeIcon />,
    },
    {
      label: 'Jira',
      url: 'https://deuna-app.atlassian.net',
      icon: <BugReportIcon />,
    },
    {
      label: 'SonarCloud',
      url: 'https://sonarcloud.io/organizations/deuna/projects',
      icon: <OpenInNewIcon />,
    },
    {
      label: 'Azure DevOps',
      url: 'https://dev.azure.com/Deuna',
      icon: <BusinessIcon />,
    },
  ];

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const query = (e.target as HTMLInputElement).value;
      if (query.trim()) {
        navigate(`/search?query=${encodeURIComponent(query.trim())}`);
      }
    }
  };

  return (
    <>
      <div className={classes.header}>
        <div className={classes.greeting}>
          <WelcomeTitle />
        </div>
        <div className={classes.searchContainer}>
          <div className={classes.searchIconWrapper}>
            <SearchIcon fontSize="small" />
          </div>
          <input
            className={classes.searchInput}
            placeholder="Search components, APIs, docs..."
            onKeyDown={handleSearchKeyDown}
          />
        </div>
      </div>
      <div className={classes.content}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <HomePageToolkit tools={quickLinks} title="Quick Links" />
          </Grid>
          <Grid item xs={12} md={4}>
            <HomePageToolkit tools={externalLinks} title="External Tools" />
          </Grid>
          <Grid item xs={12} md={4}>
            <CatalogStatsCard />
          </Grid>
          <Grid item xs={12}>
            <MyServicesCard />
          </Grid>
          <Grid item xs={12} md={6}>
            <HomePageStarredEntities />
          </Grid>
          <Grid item xs={12} md={6}>
            <HomePageTopVisited />
          </Grid>
          <Grid item xs={12}>
            <HomePageRecentlyVisited />
          </Grid>
        </Grid>
      </div>
    </>
  );
};
